import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Classnames from 'classnames';
//Style
import { useStyles } from 'components/sheets/AccountImportSheet/AccountImportSheet.style';
// Locales
import translate from 'locales/i18n';
//Mui components
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
//Pcp components
import DialogList from 'components/dialogs/commons/DialogList';
// Tool
import { ImportCSVTool } from 'tools/ImportCSVTool';
import {
    getRequiredMatchings,
    getNoneRequiredMatchings,
    getTranslation,
    isNumberType,
    getSiteMatchings,
    verifyBannedNames
} from 'components/sheets/AccountImportSheet/AccountImport.utils';
// Theme
import theme from 'assets/theme/theme';
import DialogConfirm from 'components/dialogs/commons/DialogConfirm';
//Actions
import AlertAction from 'actions/AlertAction';

const AccountImportMatchings = ({
    headerAndSamples,
    importType,
    matchings,
    setActiveStep,
    setMatching,
    setMatchings,
    setNumberFormat,
    setShowDialogConfirm,
    showDialogConfirm
}) => {

    const classes = useStyles();

    const importCSVTool = new ImportCSVTool();

    const [showDialogList, setShowDialogList] = useState(false);
    const [dialogListData, setDialogListData] = useState({});
    const [dialogAttributeName, setDialogAttributeName] = useState(false);
    const [newAttribute, setNewAttribute] = useState('');

    const openDialogList = (data, title) => {
        setDialogListData({ data, title });
        setShowDialogList(true);
    };

    const addAttribute = (attributeName) => {

        if (!newAttribute) return;

        const newMatchs = Object.assign(matchings, { [attributeName]: {
            errors: [],
            header: '',
            numberFormat: 'fr',
            parsedData: [],
            required: false,
            type: 'string',
            customName: attributeName
        } });

        setMatchings(newMatchs);
        setNewAttribute('');

    };

    const deleteAttribute = (attributeName) => {
        let newMatchs = {};
        Object.keys(matchings).forEach(key => {
            if (key !== attributeName) newMatchs = Object.assign(newMatchs,{ [key]: matchings[key] });
        });
        setMatchings(newMatchs);
    };

    const checkForDuplicateAttribute = (attributeName) => {
        let res = false;

        if (verifyBannedNames(attributeName) || attributeName.length > 30) return true;
        Object.keys(matchings).forEach(key => {
            if (key === attributeName) res = true;
        });
        return res;
    };

    const renderDialogAttribute = () => <Dialog open={dialogAttributeName} onClose={() => setDialogAttributeName(false)}>
        <DialogTitle> {'Entrez le nom de l\'attribut à ajouter'}</DialogTitle>
        <DialogContent style={{ paddingTop: '15px' }}>
            <TextField
                autoFocus
                label={'Nom de l\'attribut'}
                fullWidth
                value={newAttribute}
                onChange={(e) => setNewAttribute(e.target.value)}
            />
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setDialogAttributeName(false)}>Annuler</Button>
            <Button onClick={() => {
                if (checkForDuplicateAttribute(newAttribute)) {
                    AlertAction.open('error', 'Attribut deja existant ou interdit', 'top-right');
                    return;
                }
                addAttribute(newAttribute);
                setDialogAttributeName(false);
            }}>Ajouter</Button>
        </DialogActions>
    </Dialog>;

    const renderDialogList = () => <DialogList
        open={showDialogList}
        close={() => setShowDialogList(false)}
        dataArray={dialogListData.data}
        title={dialogListData.title}
    />;

    const renderDialogConfirm = () => <DialogConfirm
        open={showDialogConfirm}
        close={() => setShowDialogConfirm(false)}
        text={'Tous les champs n\'ont pas de match, voulez vous continuer ? \nLes champs sans match seront effacés'}
        callback={() => setActiveStep(2)}
        fullScreen={false}
    />;

    const getMatchingTableContent = matchs => {
        const supportedLocale = importCSVTool.getSupportedLocale();

        return (
            <TableBody>
                {matchs.map(matching => {
                    const [attribute] = matching;
                    const [, { header, numberFormat, errors, type }] = matching;
                    return (
                        <TableRow key={attribute} >
                            <TableCell align='left' className={Classnames(classes.basicCell, classes.firstCell)}>
                                <div style={{ fontSize: '1.3em' }}>{getTranslation(matching)}</div>
                                <div className={classes.typeContainer}>
                                    <div style={{ color: 'rgba(0, 0, 0, 0.54)' }}>{type}</div>
                                    {isNumberType(type) &&
                                        <FormControl style={{ marginLeft: '20px', width: '60%' }}>
                                            <Select
                                                value={numberFormat}
                                                style={{ color: 'rgba(0, 0, 0, 0.54)' }}
                                                onChange={e => setNumberFormat(attribute, e.target.value)}
                                            >
                                                {supportedLocale.map(locale =>
                                                    <MenuItem key={locale} value={locale}>{locale}</MenuItem>
                                                )}
                                            </Select>
                                        </FormControl>
                                    }
                                </div>
                            </TableCell>
                            <TableCell align='center' className={Classnames(classes.basicCell)}>
                                <FormControl style={{ width: '80%' }}>
                                    <InputLabel>{translate.i18n('HEADER')}</InputLabel>
                                    <Select
                                        value={header}
                                        onChange={e => setMatching(attribute, e.target.value)}
                                        label={translate.i18n('HEADER')}
                                    >
                                        <MenuItem value=''>
                                            <em>{translate.i18n('NONE')}</em>
                                        </MenuItem>
                                        {Object.keys(headerAndSamples).map(h => <MenuItem key={h} value={h} style={{ backgroundColor: Object.entries(matchings).find(m => m[1].header === h) ? theme.palette.yellow.main : null }}>{h}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </TableCell>
                            <TableCell align='center' className={importCSVTool.parsingHasError(matchings) ? Classnames(classes.basicCell) : Classnames(classes.basicCell, classes.lastCell)} >
                                <div className={Classnames(classes.cellTextContainer)} style={{ textAlign: 'left' }} >
                                    {!header ?
                                        <div>{translate.i18n('SELECT_HEADER')}</div>
                                        :
                                        headerAndSamples[header].map((cell, index) =>
                                            <div key={index} className={classes.textCell} >{(index + 2) + ' : ' + cell}</div>)
                                    }
                                </div>
                            </TableCell>
                            {importCSVTool.parsingHasError(matchings) &&
                                <TableCell align='center' className={classes.basicCell} >
                                    <div className={Classnames(classes.cellTextContainer)} onClick={() => openDialogList(errors, translate.i18n('ERROR_HEADER') + header + translate.i18n('LINK_TO_ATTRIBUTE') + getTranslation(matching))}>
                                        {!header ?
                                            <div className={classes.textCell} style={{ color: 'red' }}> {errors}</div>
                                            :
                                            errors.slice(0, 3).map((cell, index) =>
                                                <div key={index} className={classes.textCell} style={{ color: 'red', cursor: 'pointer' }}>{cell}</div>)}
                                    </div>
                                </TableCell>
                            }
                            <TableCell align='center' className={Classnames(classes.basicCell, classes.lastCell)} >
                                {matching[1].customName && <IconButton onClick={() => deleteAttribute(matching[1].customName)}> <DeleteForeverIcon /> </IconButton>}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        );
    };

    return (
        <div className={classes.tableContainer}>
            {renderDialogList()}
            {renderDialogConfirm()}
            <Table className={classes.table} aria-label='sticky table'>
                <TableHead >
                    {importCSVTool.parsingHasError(matchings) ?
                        <TableRow>
                            <TableCell align='left' className={classes.stickyTableHeadCell} style={{ paddingLeft: '15px', width: '25%' }}>{translate.i18n('ARTICLE_ATTRIBUTE')}</TableCell>
                            <TableCell align='center' className={classes.stickyTableHeadCell} style={{ width: '25%' }}>{translate.i18n('FILE_HEADER')}</TableCell>
                            <TableCell align='center' className={classes.stickyTableHeadCell} style={{ width: '25%' }}>{translate.i18n('DATA_SAMPLE')}</TableCell>
                            <TableCell align='center' className={classes.stickyTableHeadCell} style={{ width: '20%' }}>{translate.i18n('ERROR')}</TableCell>
                            <TableCell align='center' className={classes.stickyTableHeadCell} style={{ width: '5%', marginRight: '15px' }}>{translate.i18n('DELETE')}</TableCell>
                        </TableRow>
                        :
                        <TableRow>
                            <TableCell align='left' className={classes.stickyTableHeadCell} style={{ paddingLeft: '15px', width: '30%' }}>{translate.i18n('ARTICLE_ATTRIBUTE')}</TableCell>
                            <TableCell align='center' className={classes.stickyTableHeadCell} style={{ width: '30%' }}>{translate.i18n('FILE_HEADER')}</TableCell>
                            <TableCell align='center' className={classes.stickyTableHeadCell} style={{ width: '35%' }}>{translate.i18n('DATA_SAMPLE')}</TableCell>
                            {importType === 'account' && <TableCell align='center' className={classes.stickyTableHeadCell} style={{ width: '5%', marginRight: '15px' }}>{translate.i18n('DELETE')}</TableCell>}
                        </TableRow>
                    }
                </TableHead>
                <TableHead>
                    <TableRow >
                        <TableCell align='center' className={classes.tableHeadCell} colSpan={4}>
                            {translate.i18n('ATTRIBUTE_REQUIRED')}
                        </TableCell>
                    </TableRow>
                </TableHead>
                {getMatchingTableContent(getRequiredMatchings(matchings))}
                <TableHead style={{ padding: '0px', }} >
                    <TableRow>
                        <TableCell align='center' className={classes.tableHeadCell} colSpan={4}>
                            {translate.i18n('ATTRIBUTE_OPTIONAL')}
                        </TableCell>
                    </TableRow>
                </TableHead>
                {getMatchingTableContent(getNoneRequiredMatchings(matchings))}
                {importType === 'account' && <div className={classes.addAttributeButton}>
                    <Button variant='contained' onClick={() => setDialogAttributeName(true)}> Ajouter un attribut </Button>
                </div>}
                {renderDialogAttribute()}
                {importType === 'account' && <TableHead style={{ padding: '0px', }} >
                    <TableRow>
                        <TableCell align='center' className={classes.tableHeadCell} colSpan={4}>
                            {translate.i18n('SITES')}
                        </TableCell>
                    </TableRow>
                </TableHead>}
                {getMatchingTableContent(getSiteMatchings(matchings))}
            </Table>
        </div >
    );
};

AccountImportMatchings.propTypes = {
    headerAndSamples: PropTypes.object,
    importType: PropTypes.string,
    matchings: PropTypes.object,
    setActiveStep: PropTypes.func,
    setMatching: PropTypes.func,
    setMatchings: PropTypes.func,
    setNumberFormat: PropTypes.func,
    setShowDialogConfirm: PropTypes.func,
    showDialogConfirm: PropTypes.bool
};

export default AccountImportMatchings;
